import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

interface SkillExample {
  slug: string
  name: string
  category: string
  tier: number
  description: string
  example_command: string
  example_output: string
}

interface Skill {
  name: string
  description: string
  emoji?: string
  eligible: boolean
  disabled: boolean
  blockedByAllowlist?: boolean
  source?: string
  bundled?: boolean
  homepage?: string
  missing?: { bins?: string[]; env?: string[] }
}

interface SkillDetail {
  name: string
  slug?: string
  displayName?: string
  description?: string
  summary?: string
  category?: string
  author?: string
  version?: string
  emoji?: string
  homepage?: string
  eligible?: boolean
  source?: string
  skillMd?: string
  missing?: { bins?: string[]; env?: string[]; config?: string[] }
  requirements?: { bins?: string[]; env?: string[] }
}

const CATEGORY_TAGS: Record<string, { label: string; color: string; keywords: string[] }> = {
  video: { label: 'Video', color: '#E8535A', keywords: ['video', 'remotion', 'ffmpeg', 'clip', 'frame', 'subtitle', 'caption', 'movie', 'film', 'animation', 'screen'] },
  audio: { label: 'Audio', color: '#9B59B6', keywords: ['audio', 'music', 'sound', 'whisper', 'tts', 'speech', 'voice', 'podcast', 'wav', 'mp3', 'mastering'] },
  web: { label: 'Web', color: '#3498DB', keywords: ['web', 'scrape', 'html', 'css', 'http', 'url', 'site', 'page', 'browser', 'selenium', 'puppeteer', 'crawl'] },
  data: { label: 'Data', color: '#1ABC9C', keywords: ['data', 'csv', 'json', 'sql', 'database', 'parse', 'transform', 'excel', 'spreadsheet'] },
  automation: { label: 'Automation', color: '#F39C12', keywords: ['automat', 'workflow', 'schedule', 'cron', 'pipeline', 'deploy', 'ci', 'docker'] },
  coding: { label: 'Coding', color: '#2ECC71', keywords: ['code', 'git', 'github', 'test', 'lint', 'debug', 'compile', 'build', 'npm', 'python', 'typescript'] },
  image: { label: 'Image', color: '#E67E22', keywords: ['image', 'photo', 'png', 'jpg', 'svg', 'icon', 'screenshot', 'resize', 'crop', 'ocr'] },
  ai: { label: 'AI / ML', color: '#8E44AD', keywords: ['ai', 'ml', 'model', 'gpt', 'llm', 'embed', 'vector', 'neural', 'openai', 'anthropic', 'generate'] },
  devops: { label: 'DevOps', color: '#34495E', keywords: ['docker', 'kubernetes', 'aws', 'cloud', 'server', 'nginx', 'ssl', 'dns', 'monitor'] },
  productivity: { label: 'Productivity', color: '#16A085', keywords: ['note', 'calendar', 'reminder', 'obsidian', 'notion', 'todoist', 'email', 'slack', 'weather'] },
}

function inferCategory(skill: Skill): string {
  const text = `${skill.name} ${skill.description}`.toLowerCase()
  for (const [cat, info] of Object.entries(CATEGORY_TAGS)) {
    if (info.keywords.some(kw => text.includes(kw))) return cat
  }
  return 'other'
}

function renderMd(text: string): string {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h4 style="font-size:13px;font-weight:700;margin:16px 0 6px;color:var(--text-primary)">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="font-size:14px;font-weight:700;margin:20px 0 8px;color:var(--text-primary)">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 style="font-size:16px;font-weight:700;margin:20px 0 8px;color:var(--text-primary)">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:rgba(255,255,255,0.08);padding:1px 5px;border-radius:3px;font-size:12px;font-family:monospace">$1</code>')
    .replace(/^```(\w*)\n([\s\S]*?)```$/gm, (_m, _lang, code) =>
      `<pre style="background:rgba(0,0,0,0.3);padding:10px 12px;border-radius:6px;font-size:12px;overflow-x:auto;margin:8px 0;font-family:monospace;line-height:1.5">${code.trim()}</pre>`)
    .replace(/^- (.+)$/gm, '<div style="padding:2px 0 2px 16px;position:relative"><span style="position:absolute;left:4px">•</span>$1</div>')
    .replace(/\n\n/g, '<div style="height:8px"></div>')
    .replace(/\n/g, '<br/>')
}

export default function OpenClawPage() {
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [selectedSkill, setSelectedSkill] = useState<SkillDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [chatInput, setChatInput] = useState('')
  const [chatHistory, setChatHistory] = useState<{role: string; text: string}[]>([])
  const [chatLoading, setChatLoading] = useState(false)
  const [showAuditedOnly, setShowAuditedOnly] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => { searchRef.current?.focus() }, [])
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }) }, [chatHistory])

  const { data: allSkills = [] } = useQuery<Skill[]>({
    queryKey: ['openclaw-skills'],
    queryFn: async () => {
      const res = await fetch('/api/openclaw/skills')
      if (!res.ok) return []
      const data = await res.json()
      const skills: Skill[] = data.skills || data
      return skills.filter(s => !s.disabled && !s.blockedByAllowlist)
    },
    staleTime: 60_000,
  })

  const { data: examples = [] } = useQuery<SkillExample[]>({
    queryKey: ['openclaw-examples'],
    queryFn: async () => {
      const res = await fetch('/skill-examples.json')
      if (!res.ok) return []
      return res.json()
    },
    staleTime: 300_000,
  })

  const exampleMap = useMemo(() => {
    const m: Record<string, SkillExample> = {}
    for (const ex of examples) m[ex.slug] = ex
    return m
  }, [examples])

  const categorized = useMemo(() => {
    return allSkills.map(s => ({ ...s, _cat: inferCategory(s) }))
  }, [allSkills])

  const filtered = useMemo(() => {
    let list = categorized
    if (activeCategory) list = list.filter(s => s._cat === activeCategory)
    if (showAuditedOnly) list = list.filter(s => s.eligible)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(s => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q))
    }
    return list.sort((a, b) => {
      if (a.eligible !== b.eligible) return a.eligible ? -1 : 1
      return a.name.localeCompare(b.name)
    })
  }, [categorized, activeCategory, showAuditedOnly, search])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const s of categorized) {
      counts[s._cat] = (counts[s._cat] || 0) + 1
    }
    return counts
  }, [categorized])

  const loadSkillDetail = useCallback(async (name: string) => {
    setLoadingDetail(true)
    setSelectedSkill(null)
    try {
      const res = await fetch(`/api/openclaw/skills/${name}`)
      if (res.ok) setSelectedSkill(await res.json())
    } catch { /* */ }
    setLoadingDetail(false)
  }, [])

  const sendChat = useCallback(async () => {
    const msg = chatInput.trim()
    if (!msg) return
    setChatHistory(prev => [...prev, { role: 'user', text: msg }])
    setChatInput('')
    setChatLoading(true)
    try {
      const res = await fetch('/api/openclaw/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, skill: selectedSkill?.name }),
      })
      if (res.ok) {
        const data = await res.json()
        setChatHistory(prev => [...prev, { role: 'assistant', text: data.response || 'No response' }])
      }
    } catch {
      setChatHistory(prev => [...prev, { role: 'assistant', text: 'Error communicating with OpenClaw' }])
    }
    setChatLoading(false)
  }, [chatInput, selectedSkill])

  const auditedCount = allSkills.filter(s => s.eligible).length

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 44px)', overflow: 'hidden' }}>

      {/* LEFT: Browse + Search */}
      <div style={{ width: 380, minWidth: 320, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border)' }}>
        {/* Header */}
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 22 }}>🦞</span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>OpenClaw</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {allSkills.length} skills · {auditedCount} audited
              </div>
            </div>
          </div>
          <input
            ref={searchRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search skills..."
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 12px', color: 'var(--text-primary)',
              fontSize: 13, outline: 'none',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showAuditedOnly} onChange={e => setShowAuditedOnly(e.target.checked)} style={{ accentColor: '#34d399' }} />
              Audited only
            </label>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{filtered.length} shown</span>
          </div>
        </div>

        {/* Category pills */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 4, flexShrink: 0 }}>
          <button
            onClick={() => setActiveCategory(null)}
            style={{
              background: !activeCategory ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
              border: 'none', borderRadius: 12, padding: '3px 10px', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, color: !activeCategory ? 'var(--text-primary)' : 'var(--text-secondary)',
            }}
          >
            All
          </button>
          {Object.entries(CATEGORY_TAGS).map(([key, cat]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(activeCategory === key ? null : key)}
              style={{
                background: activeCategory === key ? `${cat.color}25` : 'rgba(255,255,255,0.05)',
                border: activeCategory === key ? `1px solid ${cat.color}60` : '1px solid transparent',
                borderRadius: 12, padding: '3px 10px', cursor: 'pointer',
                fontSize: 11, fontWeight: 500, color: activeCategory === key ? cat.color : 'var(--text-secondary)',
              }}
            >
              {cat.label} {categoryCounts[key] ? `(${categoryCounts[key]})` : ''}
            </button>
          ))}
        </div>

        {/* Skill list */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {filtered.map(skill => {
            const cat = CATEGORY_TAGS[skill._cat]
            const isSelected = selectedSkill?.name === skill.name
            return (
              <button
                key={skill.name}
                onClick={() => loadSkillDetail(skill.name)}
                style={{
                  display: 'flex', width: '100%', textAlign: 'left', padding: '10px 14px',
                  background: isSelected ? 'rgba(245,158,11,0.08)' : 'transparent',
                  border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  color: 'var(--text-primary)', gap: 10, alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>{skill.emoji || '📦'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{skill.name}</span>
                    <span style={{ fontSize: 10, flexShrink: 0 }}>{skill.eligible ? '✅' : '🔍'}</span>
                    {cat && <span style={{ fontSize: 9, color: cat.color, fontWeight: 600, flexShrink: 0 }}>{cat.label}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {skill.description}
                  </div>
                </div>
              </button>
            )
          })}
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 14px', color: 'var(--text-secondary)', fontSize: 13 }}>
              No skills match your filters.
            </div>
          )}
        </div>
      </div>

      {/* CENTER: Skill Detail */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, borderRight: '1px solid var(--border)' }}>
        {!selectedSkill && !loadingDetail && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>Featured Skills</h2>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                {examples.length} curated examples with live commands and output
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {examples.map(ex => (
                <button
                  key={ex.slug}
                  onClick={() => loadSkillDetail(ex.slug)}
                  style={{
                    textAlign: 'left', padding: 14, background: 'rgba(255,255,255,0.03)',
                    border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
                    color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{ex.name}</span>
                    <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(52,211,153,0.12)', color: '#34d399', fontWeight: 600 }}>
                      {ex.category}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {ex.description}
                  </div>
                  <code style={{ fontSize: 10, color: 'var(--accent)', opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                    $ {ex.example_command.replace(/^openclaw-skills /, '')}
                  </code>
                </button>
              ))}
            </div>
          </div>
        )}
        {loadingDetail && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
            Loading...
          </div>
        )}
        {selectedSkill && (
          <>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ fontSize: 32 }}>{selectedSkill.emoji || '📦'}</span>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                    {selectedSkill.displayName || selectedSkill.name}
                  </h2>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <code style={{ fontSize: 11, opacity: 0.8 }}>{selectedSkill.name}</code>
                    {selectedSkill.source && <span>· {selectedSkill.source}</span>}
                    {selectedSkill.version && <span>· v{selectedSkill.version}</span>}
                  </div>
                </div>
                <span style={{
                  fontSize: 12, padding: '4px 12px', borderRadius: 6, fontWeight: 700, flexShrink: 0,
                  background: selectedSkill.eligible ? 'rgba(52,211,153,0.15)' : 'rgba(245,158,11,0.15)',
                  color: selectedSkill.eligible ? '#34d399' : '#F59E0B',
                }}>
                  {selectedSkill.eligible ? '✅ Audited' : '🔍 Not Audited'}
                </span>
              </div>
              {selectedSkill.homepage && (
                <a href={selectedSkill.homepage} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)', opacity: 0.8 }}>
                  Documentation ↗
                </a>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', minHeight: 0 }}>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-primary)', marginTop: 0, marginBottom: 20 }}>
                {selectedSkill.description || selectedSkill.summary}
              </p>

              {exampleMap[selectedSkill.name] && (
                <div style={{ marginBottom: 20, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.08)', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 4 }}>Example</div>
                    <code style={{ fontSize: 12, color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                      $ {exampleMap[selectedSkill.name].example_command}
                    </code>
                  </div>
                  <div style={{ padding: '10px 14px', background: 'rgba(0,0,0,0.2)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase' }}>Output</div>
                    <pre style={{ fontSize: 12, color: '#34d399', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.5 }}>
                      {exampleMap[selectedSkill.name].example_output}
                    </pre>
                  </div>
                </div>
              )}

              {selectedSkill.missing && (selectedSkill.missing.bins?.length || selectedSkill.missing.env?.length) ? (
                <div style={{ padding: 14, background: 'rgba(239,68,68,0.08)', borderRadius: 10, marginBottom: 20, border: '1px solid rgba(239,68,68,0.2)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#ef4444', marginBottom: 8, textTransform: 'uppercase' }}>Missing Requirements</div>
                  {selectedSkill.missing.bins?.map(b => <code key={b} style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: 4, marginRight: 6, fontSize: 11 }}>{b}</code>)}
                  {selectedSkill.missing.env?.map(e => <code key={e} style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: 4, marginRight: 6, fontSize: 11 }}>{e}</code>)}
                </div>
              ) : null}

              {selectedSkill.requirements?.bins?.length ? (
                <div style={{ padding: 12, background: 'rgba(52,211,153,0.06)', borderRadius: 10, marginBottom: 20, border: '1px solid rgba(52,211,153,0.15)' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#34d399', textTransform: 'uppercase', marginBottom: 6 }}>Requirements</div>
                  {selectedSkill.requirements.bins.map(b => <code key={b} style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: 4, marginRight: 8, fontSize: 11 }}>{b}</code>)}
                </div>
              ) : null}

              {selectedSkill.skillMd ? (
                <div
                  style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--text-primary)' }}
                  dangerouslySetInnerHTML={{ __html: renderMd(selectedSkill.skillMd) }}
                />
              ) : (
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontStyle: 'italic', padding: '20px 0' }}>
                  No detailed usage instructions available for this skill.
                </div>
              )}

              <div style={{ marginTop: 24, padding: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase' }}>CLI</div>
                <code style={{ fontSize: 12, color: 'var(--accent)' }}>openclaw-skills info {selectedSkill.name}</code>
              </div>
            </div>
          </>
        )}
      </div>

      {/* RIGHT: Chat */}
      <div style={{ width: 360, minWidth: 300, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            @openclaw
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            Describe what you need — I'll find the right skills
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0', minHeight: 0 }}>
          {chatHistory.length === 0 && (
            <div style={{ padding: '32px 16px', color: 'var(--text-secondary)', fontSize: 13 }}>
              <div style={{ fontStyle: 'italic', marginBottom: 20, textAlign: 'center', lineHeight: 1.6 }}>
                Ask me anything about what's available. I'll search the registry and explain what each skill does.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  'Edit video clips and add captions',
                  'Scrape data from a website',
                  'Generate audio from text',
                  'Browser automation for testing',
                  'What can I use for image processing?',
                ].map(q => (
                  <button
                    key={q}
                    onClick={() => { setChatInput(q); }}
                    style={{
                      background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)',
                      borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
                      color: 'var(--text-secondary)', fontSize: 12, textAlign: 'left',
                    }}
                  >
                    "{q}"
                  </button>
                ))}
              </div>
            </div>
          )}
          {chatHistory.map((msg, i) => (
            <div key={i} style={{ padding: '4px 16px', display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                background: msg.role === 'user' ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.06)',
                borderRadius: 10, padding: '8px 12px', maxWidth: '92%',
                fontSize: 13, lineHeight: 1.5, color: 'var(--text-primary)', wordBreak: 'break-word',
              }}>
                {msg.role === 'assistant' ? (
                  <span dangerouslySetInnerHTML={{ __html: renderMd(msg.text) }} />
                ) : msg.text}
              </div>
            </div>
          ))}
          {chatLoading && (
            <div style={{ padding: '4px 16px' }}>
              <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '8px 12px', maxWidth: '90%', fontSize: 13, color: 'var(--text-secondary)' }}>
                Searching...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexShrink: 0 }}>
          <input
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && chatInput.trim()) { e.preventDefault(); sendChat() } }}
            placeholder="What do you need?"
            style={{
              flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 12px', color: 'var(--text-primary)',
              fontSize: 13, outline: 'none',
            }}
          />
          <button
            onClick={sendChat}
            disabled={chatLoading || !chatInput.trim()}
            style={{
              background: chatInput.trim() ? 'var(--accent, #F59E0B)' : 'rgba(255,255,255,0.06)',
              color: chatInput.trim() ? '#000' : 'var(--text-muted)',
              border: 'none', borderRadius: 8, padding: '8px 16px', cursor: chatInput.trim() ? 'pointer' : 'default',
              fontSize: 13, fontWeight: 600,
            }}
          >
            Ask
          </button>
        </div>
      </div>
    </div>
  )
}
